import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type { User } from "@contracts/user";
import { connectSocket, disconnectSocket } from "@/lib/socket";
import { apiUrl } from "@/lib/api";

async function fetchUser(): Promise<User | null> {
  const response = await fetch(apiUrl("/api/auth/user"), {
    credentials: "include",
  });

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }

  return response.json();
}

async function logout(): Promise<void> {
  window.location.href = apiUrl("/api/logout");
}

async function syncTimezone(): Promise<void> {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  try {
    await fetch(apiUrl("/api/profile/timezone"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ timezone: tz }),
      credentials: "include",
    });
  } catch {
    /* ignore */
  }
}

export function useAuth() {
  const queryClient = useQueryClient();
  const timezoneSynced = useRef(false);
  const { data: user, isLoading } = useQuery<User | null>({
    queryKey: ["/api/auth/user"],
    queryFn: fetchUser,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (user && !timezoneSynced.current) {
      timezoneSynced.current = true;
      void syncTimezone();
      connectSocket(user.id);
    }
    if (!user) {
      disconnectSocket();
    }
  }, [user]);

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.setQueryData(["/api/auth/user"], null);
    },
  });

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
  };
}
